/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';
import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError, isApiFilterValid } from 'Utils';
import { fetchPost } from 'FetchAPIs';
import { LocationType, ResourceSatus } from 'HierarchyTypes';
import { ApiFilter } from 'CommonTypes';
import { ResultsTotalCountType } from 'HierarchyActionTypes';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingLocationResources: 'Error loading locations. {message}',
});

export const getLocationResources = async ({
  userEmailId,
  bookmark = 0,
  limit = 100,
  filters = {},
}: {
  userEmailId: string;
  bookmark?: number;
  limit?: number;
  filters?: ApiFilter;
}): Promise<ResultsTotalCountType<LocationType>> =>
  fetchPost(
    `${baseHierarchiesURL}/workspaces/resources/locations/search`,
    {
      search: filters.name ?? '',
      filter: { ...pickBy(omit(filters, ['name']), isApiFilterValid) },
    },
    { userEmailId },
    { offset: bookmark / limit, limit, status: ResourceSatus.ACTIVE }
  )
    .then(response => response ?? { results: [], totalCount: 0 }) // Fixing for the scenario where the API returns 204 and empty response (when search criteria is not met)
    .catch(error => {
      throw new I18NError(messages.errorLoadingLocationResources, {
        message: formatErrorResponse(error),
      });
    });
